import Plotly from "plotly.js-dist-min";
import usdaLogoUrl from "@fsa-style/src/img/usda-logo.svg";

import { convertMonthAndDayToPeriodLabel, convertPeriodToMonthsAndDays } from "./forecast-evolution-utils.js";

export function getLine(y, text, xanchor, yanchor, color, textPosition, visibility = "legendonly") {
    const line = {
        type: "line",
        showlegend: true,
        visible: visibility,
        layer: "above",
        xref: "paper",
        x0: 0,
        x1: 1,
        y0: y,
        y1: y,
        name: text,
        label: {
            font: {
                weight: 400,
                shadow: `${color} 0px 0px 5px`,
                color: "#000",
            },
            layer: "above",
            text: `${text} (${Math.round(y).toLocaleString()} AF)`,
            xanchor: xanchor,
            yanchor: yanchor,
            textposition: textPosition,
        },
        line: {
            color: color,
            dash: "dash",
            width: 2,
        },
    };
    return line;
}

const activeIcon = {
    width: 512,
    height: 512,
    path: "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z",
};

const activeOnly = {
    name: "Toggle active legend items only",
    icon: activeIcon,
    toggle: true,
    direction: "up",
    attr: "toggled",
    val: null,
    click: async (gd, ev) => {
        const button = ev.currentTarget;
        const val = button.classList.contains("active");
        const updated_shapes = {};
        const update_traces = [];
        let update = {};
        if (!val) {
            // active only is currently toggled ON. toggle it OFF

            gd.layout.shapes.forEach((shape, shp_idx) => {
                if (shape.type === "line") {
                    if (shape.visible === false) {
                        updated_shapes[`shapes[${shp_idx}].visible`] = "legendonly";
                    }
                }
            });
            gd.data.forEach((trc, trc_idx) => {
                if (trc.visible === false) {
                    update = { visible: "legendonly" };
                    update_traces.push(trc_idx);
                }
            });
        } else {
            // active only is current toggled OFF. toggle it ON
            gd.layout.shapes.forEach((shape, shp_idx) => {
                if (shape.type === "line") {
                    if (shape.visible === "legendonly") {
                        updated_shapes[`shapes[${shp_idx}].visible`] = false;
                    }
                }
            });
            gd.data.forEach((trc, trc_idx) => {
                if (trc.visible === "legendonly") {
                    update = { visible: false };
                    update_traces.push(trc_idx);
                }
            });
        }
        Plotly.relayout(document.getElementById("plot"), updated_shapes);
        Plotly.restyle(document.getElementById("plot"), update, update_traces);
    },
};

export function getConfig() {
    const baseConfig = {
        displaylogo: false,
        responsive: true,
        modeBarButtonsToAdd: [activeOnly],
    };
    return baseConfig;
}

export function getLayout() {
    const baseLayout = {
        title: {
            text: "<b>Forecast Evolution</b>",
            subtitle: {
                font: {
                    size: 13,
                },
            },
        },
        xaxis: {
            hoverformat: "%b %d",
            type: "date",
            tickmode: "linear",
            tickformat: "%b<br>%Y",
            tickfont: {
                size: 14,
                weight: "600",
            },
            dtick: "M1",
            autorange: false,
        },
        yaxis: {
            range: [0, 1],
            title: {
                font: {
                    size: 15,
                    weight: "bold",
                },
                text: "Stream Volume (Acre-Feet)",
            },
            tickFont: {
                size: 13,
            },
        },
        margin: { b: 50, t: 50, l: 70, r: 50, pad: 0 },
        images: [
            {
                source: usdaLogoUrl,
                xref: "paper",
                yref: "paper",
                x: 1.07,
                y: 0.99,
                xanchor: "right",
                yanchor: "top",
                sizex: 0.23,
                sizey: 0.15,
                sizing: "contain",
                opacity: 0.3,
                layer: "below",
            },
        ],
        legend: {
            //x: 0,
            //y: -0.12,
            groupclick: "togglegroup",
            traceorder: "grouped",
            font: {
                size: 14,
                color: "#000",
            },
            bgcolor: "#F6F6F6",
            bordercolor: "#ECECEC",
            borderwidth: 2,
            //orientation: "h",
        },
        autosize: true,
        barmode: "relative",
        hovermode: "x unified",
    };
    return baseLayout;
}

export function createEmptyPlot(message = 'Click "Load Data" to create a<br> forecast evolution plot') {
    const plotID = "plot";
    const config = getConfig();
    const layout = getLayout();

    // Add the text
    layout.annotations = [
        {
            xref: "paper",
            yref: "paper",
            x: 0.5,
            y: 0.5,
            xanchor: "middle",
            yanchor: "middle",
            showarrow: false,
            text: message,
            font: {
                size: 24,
                color: "#1e1e1e",
            },
            bordercolor: "#1e1e1e",
            borderwidth: 2,
            borderpad: 7,
            bgcolor: "#f2f2f2",
            opacity: 0.7,
        },
    ];

    // Create the plot
    Plotly.newPlot(plotID, [], layout, config);
}

export function initializeForecastTraces(guidance = false) {
    const traces = [];
    const colorMap = {
        Low: "#D20103",
        "70%": "#FFBF01",
        "50%": "#7DDA58",
        "30%": "#5DE2E7",
        High: "#CC6CE7",
    };
    const fillColorA = guidance ? "#E2EAF477" : "#3BBB5F38";
    const fillColorB = guidance ? "#E2EAF477" : "#5FE68538";
    for (const exc of ["Low", "70%", "50%", "30%", "High"].reverse()) {
        traces.push({
            x: [],
            y: [],
            customdata: [],
            legendgroup: guidance ? "Guidance" : "Forecasts",
            legendgrouptitle: { text: guidance ? "Guidance Forecasts" : "Official Forecasts" },
            mode: guidance ? "lines" : "lines+markers+text",
            hoveron: "points",
            name: exc,
            showlegend: true,
            text: [],
            textposition: "top",
            marker: {
                color: colorMap[exc],
                line: {
                    color: "#2e2e2e",
                    width: guidance ? 0.5 : 1,
                },
                size: guidance ? 4 : 8,
                symbol: "circle",
            },
            line: {
                color: guidance ? "#4e4e4e" : "#2e2e2e",
                width: exc === "50%" ? (guidance ? 0.5 : 1) : 0.5,
            },
            fill: exc === "High" ? "none" : "tonexty",
            fillcolor: ["30%", "Low"].includes(exc) ? fillColorA : fillColorB,
            yhoverformat: ",.3r",
            hoverinfo: "name+x+y",
        });
    }

    return traces;
}

/**
 *
 * @param {*} observations
 * @param {*} forecasts
 * @param {*} stats
 */
export function createEvolutionPlot(observations, forecasts, stats, period, name, guidanceForecasts = []) {
    // Create observed plotly trace
    const obsDates = Object.keys(observations).sort((a, b) => a - b);
    const obs = obsDates.map((dt) => observations[dt]);
    const obsTrace = {
        x: obsDates.map((o) => new Date(o + "T00:00")),
        y: obs,
        mode: "lines",
        fill: "tozeroy",
        hoveron: "points",
        name: "Observed",
        line: {
            color: "#9BDAF1",
        },
        showlegend: true,
        yhoverformat: ",.3r",
    };

    // Create guidance traces if needed
    let guidTraces = [];
    if (guidanceForecasts.length > 0) {
        guidTraces = initializeForecastTraces(true);
        const guidPubDates = guidanceForecasts.map((f) => f.publicationDate);
        guidPubDates.forEach((pubDate) => {
            const pubDateJs = new Date(pubDate.slice(0, 10) + "T00:00");
            const forecast = guidanceForecasts.filter((f) => f.publicationDate === pubDate)[0];
            const valueObj = forecast.forecastValues;
            Object.keys(valueObj).forEach((key, index) => {
                guidTraces[index].x.push(pubDateJs);
                guidTraces[index].y.push(valueObj[key]);
                guidTraces[index].text.push("");
                guidTraces[index].customdata.push(forecast.forecastPeriod);
            });
        });
    }

    // Create the forecast traces
    const fcstPubDates = forecasts.map((f) => f.publicationDate);
    const fcstTraces = initializeForecastTraces();
    const derivedFromTrace = {
        x: [],
        y: [],
        text: [],
        hoveron: "points",
        showlegend: false,
        marker: {
            size: 0,
            color: "#ffffff00",
        },
        line: {
            color: "#ffffff00",
        },
        hoverinfo: "text",
    };
    fcstPubDates.forEach((pubDate) => {
        const pubDateJs = new Date(pubDate.slice(0, 10) + "T00:00");
        const forecast = forecasts.filter((f) => f.publicationDate === pubDate)[0];

        const fPeriod = forecast.forecastPeriod
            .map((p) => convertMonthAndDayToPeriodLabel(+p.split("-")[0], +p.split("-")[1]))
            .join("-");
        const valueObj = forecast.forecastValues;
        Object.keys(valueObj).forEach((key, index) => {
            fcstTraces[index].x.push(pubDateJs);
            fcstTraces[index].y.push(valueObj[key]);
            fcstTraces[index].customdata.push(forecast.forecastPeriod);
            if (key === "10") {
                if (fPeriod != period) {
                    derivedFromTrace.x.push(pubDateJs);
                    derivedFromTrace.y.push(valueObj[key]);
                    derivedFromTrace.text.push("Derived from <em>" + fPeriod + "</em> forecast");
                }

                fcstTraces[index].text.push(forecast.forecastStatus != "final" ? forecast.forecastStatus : "");
            }
        });
    });

    // Create the stats lines
    const year = +fcstPubDates[fcstPubDates.length - 1].slice(0, 4);
    const { total, median, avg, min, max } = stats;

    let lineShapes = [
        getLine(total, `${year} Total`, "right", "bottom", "#60B0CD", "end", true),
        getLine(median, "30-Yr Median", "left", median > avg ? "bottom" : "top", "#CE800B", "start", true),
        getLine(avg, "30-Yr Average", "left", median > avg ? "top" : "bottom", "#BFD641", "start"),
        getLine(min, "POR Min", "left", "bottom", "#EA7476", "start", true),
        getLine(max, "POR Max", "left", "top", "#F6AFD8", "start", true),
    ];

    // filter out NaN's
    lineShapes = lineShapes.filter((l) => !isNaN(l.y0));

    // Get Layout
    const layout = getLayout();

    // Set ranges
    const minObs = new Date(Math.min.apply(null, obsTrace.x));
    const minFcst = new Date(Math.min.apply(null, fcstTraces[0].x));

    const [[m1, d1], [m2, d2]] = convertPeriodToMonthsAndDays(period);
    const startOfPeriod = new Date(`${year}-${m1.toString().padStart(2, "0")}-${d1.toString().padStart(2, "0")}T00:00`);
    const endOfPeriod = new Date(`${year}-${m2.toString().padStart(2, "0")}-${d2.toString().padStart(2, "0")}T00:00`);

    // Create a gray rectangel showing the forecast period
    const rectShape = {
        type: "rect",
        xref: "x",
        yref: "paper",
        x0: startOfPeriod,
        x1: endOfPeriod,
        y0: 0,
        y1: 1,
        fillcolor: "#d3d3d319",
        line: { width: 0 },
    };

    const firstDate = (minObs < minFcst ? minObs : minFcst).addDays(-33);

    layout.xaxis.range = [firstDate, endOfPeriod];
    layout.xaxis.tick0 = new Date(`${firstDate.getFullYear()}-${firstDate.getMonth()}-01T00:00`);

    // Figure out yRange. normally, just a buffered POR min/max, but there are
    // cases where the forecasted flows are greater than POR max
    const yMin = Math.min(0, min - 0.05 * (max - min), ...fcstTraces[4].y);
    const yMax = Math.max(max + 0.1 * (max - min), ...fcstTraces[0].y);
    layout.yaxis.range = [yMin, yMax];

    // Set title
    layout.title.subtitle.text = `${name}<br>WY${year} ${period}`;

    // set shapes
    layout.shapes = [rectShape, ...lineShapes];

    // Config
    const config = getConfig();

    Plotly.newPlot("plot", [...guidTraces, derivedFromTrace, ...fcstTraces, obsTrace], layout, config);
}
